// frontend/src/pages/ConvitePage/ConvitePage.jsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, Button, Typography, Alert, Spin, Image, Space } from 'antd';
import { useAuth } from '../../contexts/AuthContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { getConvite, aceitarConvite, recusarConvite } from '../../services/compartilhamentoService';
import { getErrorMessage } from '../../utils/errorHandler';
import logo from '../../assets/images/logo_vertical.jpg';
import '../../styles/auth.css';

const { Title, Text } = Typography;

export default function ConvitePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const workspace = useWorkspace() || {};
  const { recarregarCompartilhamento, trocarWorkspace } = workspace;

  const [convite, setConvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acao, setAcao] = useState(false);
  const [erro, setErro] = useState('');

  // Guarda o token para retomar o fluxo depois do login/cadastro.
  useEffect(() => {
    try {
      if (token) sessionStorage.setItem('convite_pendente', token);
    } catch { /* ignore */ }
  }, [token]);

  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }
    // Já logado: consome o token pendente e busca o convite.
    try { sessionStorage.removeItem('convite_pendente'); } catch { /* ignore */ }
    (async () => {
      try {
        setConvite(await getConvite(token));
      } catch (e) {
        setErro(getErrorMessage(e, 'Convite não encontrado.'));
      } finally {
        setLoading(false);
      }
    })();
  }, [token, isAuthenticated]);

  const aceitar = async () => {
    setAcao(true);
    setErro('');
    try {
      const acesso = await aceitarConvite(token);
      if (recarregarCompartilhamento) await recarregarCompartilhamento();
      if (trocarWorkspace) trocarWorkspace(acesso.titular?.id); // entra no workspace (faz reload)
      else window.location.assign('/meu-desempenho');
    } catch (e) {
      setErro(getErrorMessage(e, 'Não foi possível aceitar o convite.'));
      setAcao(false);
    }
  };

  const recusar = async () => {
    setAcao(true);
    try {
      await recusarConvite(token);
    } catch { /* ignore */ }
    // Reload completo: o usuário pode ter ganhado o trial ao recusar.
    window.location.assign('/meu-desempenho');
  };

  const emailBate = convite
    && (user?.email || '').toLowerCase() === (convite.convite_email || '').toLowerCase();

  const cadastroUrl = `/cadastro?redirect=${encodeURIComponent(`/convite/${token}`)}`;
  const loginUrl = `/login?redirect=${encodeURIComponent(`/convite/${token}`)}`;

  return (
    <div className="auth-shell">
      <Card className="auth-card auth-card--wide">
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <Image width={140} src={logo} alt="Saldo Ativo" preview={false} className="auth-logo" />
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 24 }}><Spin /></div>
        ) : !isAuthenticated ? (
          <>
            <Title level={4} style={{ textAlign: 'center' }}>Você recebeu um convite</Title>
            <Text style={{ display: 'block', textAlign: 'center', marginBottom: 20 }}>
              Para acompanhar as finanças e milhas de quem te convidou, entre ou crie sua
              conta no Saldo Ativo — use o <strong>mesmo e-mail</strong> em que você recebeu
              o convite. Depois voltamos automaticamente para cá.
            </Text>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Button type="primary" size="large" block onClick={() => navigate(cadastroUrl)}>
                Criar minha conta
              </Button>
              <Button size="large" block onClick={() => navigate(loginUrl)}>Já tenho conta</Button>
            </Space>
          </>
        ) : erro && !convite ? (
          <>
            <Alert type="error" showIcon message={erro} style={{ marginBottom: 16 }} />
            <Button block onClick={() => navigate('/meu-desempenho')}>Ir para o Saldo Ativo</Button>
          </>
        ) : convite?.ja_aceito_por_voce ? (
          <>
            <Title level={4} style={{ textAlign: 'center' }}>Você já aceitou este convite</Title>
            <Button type="primary" block onClick={() => trocarWorkspace(convite.titular?.id)}>
              Abrir a conta de {convite.titular?.nome}
            </Button>
          </>
        ) : convite?.status !== 'pendente' ? (
          <>
            <Title level={4} style={{ textAlign: 'center' }}>Convite indisponível</Title>
            <Text type="secondary" style={{ display: 'block', textAlign: 'center', marginBottom: 16 }}>
              Este convite já foi usado ou não está mais válido.
            </Text>
            <Button block onClick={() => navigate('/meu-desempenho')}>Ir para o Saldo Ativo</Button>
          </>
        ) : (
          <>
            <Title level={4} style={{ textAlign: 'center' }}>Convite de acesso compartilhado</Title>
            <Text style={{ display: 'block', textAlign: 'center', marginBottom: 16 }}>
              <strong>{convite.titular?.nome}</strong> ({convite.titular?.email}) convidou você
              para acompanhar e organizar junto as finanças e milhas na conta dele.
            </Text>

            {!emailBate && (
              <Alert
                type="warning" showIcon style={{ marginBottom: 16 }}
                message="Este convite foi enviado para outro e-mail"
                description={(
                  <>
                    Entre com a conta <strong>{convite.convite_email}</strong> para aceitar.{' '}
                    <Link to={loginUrl}>Trocar de conta</Link>
                  </>
                )}
              />
            )}
            {erro && <Alert type="error" showIcon message={erro} style={{ marginBottom: 16 }} />}

            <Space direction="vertical" style={{ width: '100%' }}>
              <Button type="primary" size="large" block loading={acao} disabled={!emailBate} onClick={aceitar}>
                Aceitar convite
              </Button>
              <Button size="large" block disabled={acao} onClick={recusar}>Recusar</Button>
            </Space>

            <Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 16 }}>
              Você entra com o seu próprio login. Cada lançamento fica marcado com quem fez.
              Dá para voltar para a sua conta quando quiser.
            </Text>
          </>
        )}
      </Card>
    </div>
  );
}
